import { useRef, useEffect } from 'react';
import { Box, Button, IconButton, Slide, useMediaQuery } from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { homePageService } from 'app/utils/services/homePageService';
import { NotificationWithTime } from 'app/utils/groupNotificationsByDate';
import { greyText, grey12, black1, white } from 'app/components/ThemeProvider/colors';
import { getTheme } from 'app/components/ThemeProvider/theme';
import DrawerCloseIcon from 'app/components/Icons/DrawerCloseIcon';
import YeloLogo from 'app/components/Icons/YeloLogo';
import {
  notificationBackdrop,
  notificationCloseButton,
  notificationList,
  notificationLogo,
  notificationMobileHeader,
  notificationOverlay,
  notificationHeader,
  notificationTitle,
  notificationDot,
  NotificationItem,
} from './NotificationBar.sx';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';

interface NotificationBarProps {
  open: boolean;
  onClose: () => void;
  notificationIconRef?: React.RefObject<HTMLElement>;
}

const NotificationBar = ({ open, onClose, notificationIconRef }: NotificationBarProps) => {
  const notificationRef = useRef<HTMLDivElement>(null);
  const { data: notifications, refetch } = useQuery(['notifications'], () =>
    homePageService.getNotifications()
  );
  const { mutate: notificationMutation } = useMutation('notification', (notificationId: string) =>
    homePageService?.changeNotificationStatus(notificationId)
  );
  const navigate = useCustomNavigate();
  const mediumScreen = useMediaQuery(getTheme().breakpoints.down('md'));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isNotificationIcon = notificationIconRef?.current?.contains(target);

      if (
        notificationRef.current &&
        !notificationRef.current.contains(target) &&
        !isNotificationIcon
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, notificationIconRef]);

  const unReadNotificationsCount = notifications?.reduce((acc, item) => {
    return (
      acc +
      item?.items?.filter((notification: NotificationWithTime) => notification?.read === 'unread')
        ?.length
    );
  }, 0);

  return (
    <>
      {open && <Box sx={notificationBackdrop} onClick={onClose} />}
      <Slide direction={mediumScreen ? 'right' : 'left'} in={open} mountOnEnter unmountOnExit>
        <Box sx={notificationOverlay} ref={notificationRef}>
          <Box sx={notificationMobileHeader}>
            <YeloLogo sx={notificationLogo} />
            <IconButton sx={notificationCloseButton} disableRipple onClick={onClose}>
              <DrawerCloseIcon />
            </IconButton>
          </Box>
          <Box sx={notificationHeader}>
            <Box sx={notificationTitle}>
              Bildirişlər {unReadNotificationsCount > 0 ? `(${unReadNotificationsCount})` : ''}
            </Box>
          </Box>
          <Box sx={notificationList}>
          {notifications?.map(({ date, items }) => {
            return (
              <Box key={date}>
                <Box
                  sx={{
                    color: greyText,
                    p: '16px 32px',
                  }}
                >
                  {date}
                </Box>
                {items?.map((item: NotificationWithTime) => (
                  <NotificationItem
                    key={item?.id}
                    isRead={item?.read === 'unread'}
                    onClick={() => {
                      if (item?.read === 'unread') {
                        notificationMutation(item?.id, {
                          onSuccess: () => {
                            refetch();
                          },
                        });
                      }
                    }}
                  >
                    <Box display='flex' alignItems='flex-start' sx={{ width: '100%' }}>
                      <Box marginRight={2}>
                        <img src={`/images/${item?.type ?? 'warning'}.svg`} alt='info' />
                      </Box>
                      <Box maxWidth={323}>
                        <Box fontWeight={700} fontSize={16}>
                          {item?.title}
                        </Box>
                        <Box fontWeight={400} fontSize={12} color={grey12} marginBottom={2}>
                          {item?.description}
                        </Box>
                        {item?.notification_link && (
                          <Button
                            size='small'
                            sx={{
                              backgroundColor: black1,
                              color: white,
                              '&:hover': {
                                background: black1,
                              },
                            }}
                            onClick={() => {
                              onClose();
                              if (item?.notification_link?.startsWith('http')) {
                                window.open(item?.notification_link, '_blank');
                              } else {
                                navigate(item?.notification_link?.slice(1));
                              }
                            }}
                          >
                            Keçid et
                          </Button>
                        )}
                      </Box>
                      <Box fontWeight={400} fontSize={12} color={grey12} marginLeft='auto'>
                        {item?.time}
                      </Box>
                    </Box>
                    {item?.read === 'unread' && <Box sx={notificationDot}></Box>}
                  </NotificationItem>
                ))}
              </Box>
            );
          })}
          </Box>
        </Box>
      </Slide>
    </>
  );
};

export default NotificationBar;
